import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { TextButton } from '@/components/common/text-button';
import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { OtpField } from '@/components/forms';
import { useOtpChallenge } from '@/features/auth';
import { ApiError } from '@/lib/api/errors';
import { mutationErrorMessage, mutationErrorTone } from '@/lib/api/mutation-toast';

import { CODE_ENTRY_COPY, PROFILE_ERROR_CODES } from '../../constants';
import type { CodeDispatch } from '../../schemas';

import styles from './otp-step-modal.module.scss';

const CODE_LENGTH = 6;
// Any string passes: the length gate is the disabled Verify button, and a passing resolver is what
// clears a server `OTP_INVALID` on the next edit (RHF re-validates on change after a submit).
const formSchema = z.object({ otp: z.string() });

/** The server's ways of saying the code is gone, on any status (verify or resend). */
const EXPIRY_CODES = new Set<string>([
  PROFILE_ERROR_CODES.OTP_EXPIRED,
  PROFILE_ERROR_CODES.OTP_SESSION_NOT_FOUND,
  PROFILE_ERROR_CODES.OTP_SESSION_EXPIRED,
]);

/** True when the server says the code is gone; the caller then leaves through `expire`. */
function isExpiry(error: unknown) {
  return error instanceof ApiError && EXPIRY_CODES.has(error.code ?? '');
}

export type OtpStepModalProps = {
  /** The send-step dispatch; the modal then owns the `referenceId` and rotates it on resend. */
  dispatch: CodeDispatch;
  /** Verifies the code against this step's route; resolves on success, rejects with an ApiError. */
  verify: (referenceId: string, otp: string) => Promise<unknown>;
  /** Re-sends the code; resolves with the new dispatch (a rotated `referenceId`). */
  resend: (referenceId: string) => Promise<CodeDispatch>;
  onVerified: () => void;
  /** The code lapsed (timer or server). The caller toasts and leaves; called at most once. */
  onExpired: () => void;
  onClose: () => void;
  /** A verify failure the caller presents itself (e.g. verify-new 409); return true when handled. */
  onVerifyError?: (error: unknown) => boolean;
};

/**
 * The shared "Enter the 6 Digit Code" step (Figma 1790:983884) — Change Password's Step 2 and both
 * Phone-change code steps. `OTP_INVALID` renders inline (red boxes + message, Verify disabled until
 * the code is edited); every other failure is a toast. The modal runs both timers — the resend
 * cooldown (shown as mm:ss) and the code's validity window (never shown) — rotates the
 * `referenceId` on every resend, and reports expiry, from the timer or the server, once via
 * `onExpired`.
 */
export function OtpStepModal({
  dispatch,
  verify,
  resend,
  onVerified,
  onExpired,
  onClose,
  onVerifyError,
}: OtpStepModalProps) {
  const toast = useToast();
  const [referenceId, setReferenceId] = useState(dispatch.referenceId);
  // A ref, not state: two clicks inside one render must not both send.
  const resendingRef = useRef(false);
  const challenge = useOtpChallenge(dispatch);
  // The timer and a server code can both land; the exit happens once.
  const expiredRef = useRef(false);

  const expire = useCallback(() => {
    if (expiredRef.current) return;
    expiredRef.current = true;
    onExpired();
  }, [onExpired]);

  // The 10-minute window is never displayed — only acted on (US-117548 5e).
  useEffect(() => {
    if (challenge.expired) expire();
  }, [challenge.expired, expire]);

  async function onResend() {
    if (challenge.attemptsRemaining === 0) {
      toast.warning({ title: CODE_ENTRY_COPY.TOO_MANY }); // nothing sent (5j)
      return;
    }
    if (resendingRef.current) return; // single-submit
    resendingRef.current = true;
    try {
      const next = await resend(referenceId);
      setReferenceId(next.referenceId);
      challenge.onResend(next);
    } catch (error) {
      if (isExpiry(error)) return expire();
      if (
        error instanceof ApiError &&
        error.code === PROFILE_ERROR_CODES.RESEND_RATE_LIMIT_EXCEEDED
      ) {
        challenge.exhaustAttempts();
        toast.warning({ title: CODE_ENTRY_COPY.TOO_MANY });
        return;
      }
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.RESEND_COOLDOWN_ACTIVE) {
        const seconds = Number(error.details?.resendCooldownSeconds);
        challenge.restartCooldown(
          Number.isFinite(seconds) ? seconds : dispatch.resendCooldownSeconds,
        );
        return;
      }
      toast[mutationErrorTone(error)]({ title: mutationErrorMessage(error) });
    } finally {
      resendingRef.current = false;
    }
  }
  const { control, handleSubmit, setError, formState } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { otp: '' },
  });
  const otp = useWatch({ control, name: 'otp' });

  // Built per click, not during render: the handler reads `expire`'s ref guard, and the React
  // Compiler lint (`react-hooks/refs`) rejects a ref-reading closure handed to `handleSubmit` in render.
  const onSubmit = () =>
    handleSubmit(async ({ otp: code }) => {
      try {
        await verify(referenceId, code);
        onVerified();
      } catch (error) {
        if (isExpiry(error)) return expire();
        if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.OTP_INVALID) {
          setError('otp', { type: 'server', message: CODE_ENTRY_COPY.INVALID });
          return;
        }
        if (onVerifyError?.(error)) return;
        toast[mutationErrorTone(error)]({ title: mutationErrorMessage(error) });
      }
    })();

  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={CODE_ENTRY_COPY.TITLE}
      subtitle={CODE_ENTRY_COPY.SUBTITLE(dispatch.maskedPhone.slice(-4))}
      primaryText="Verify Code"
      onPrimary={() => void onSubmit()}
      primaryDisabled={otp.length < CODE_LENGTH || Boolean(formState.errors.otp)}
      loading={formState.isSubmitting}
    >
      <div className={styles.body}>
        <OtpField
          name="otp"
          control={control}
          length={CODE_LENGTH}
          label={CODE_ENTRY_COPY.LABEL}
          required
          autoFocus
        />
        <p className={styles.resend}>
          {challenge.resendSecondsLeft > 0 ? (
            CODE_ENTRY_COPY.COOLDOWN(challenge.resendSecondsLeft)
          ) : (
            // Clickable even at 0 attempts: the click then toasts instead of sending (5j).
            <>
              <TextButton
                htmlType="button"
                className={styles.resendButton}
                onClick={() => void onResend()}
              >
                {CODE_ENTRY_COPY.RESEND}
              </TextButton>{' '}
              <span className={styles.attempts}>
                {CODE_ENTRY_COPY.ATTEMPTS(challenge.attemptsRemaining)}
              </span>
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}
